/* ──────────────────────────────────────────────────────────────────────────
 * Kumbh Digital Twin — the graph (§4).
 *
 * entities.js is the catalogue. This is what turns it into a twin: a typed,
 * directed graph plus the live state from the running model, so the platform
 * can answer questions a map layer cannot —
 *
 *   "What does closing BAR-01 affect?"          → downstream traversal
 *   "What feeds Ramkund?"                       → upstream traversal
 *   "Which agency owns the binding constraint?" → entity → agency
 *   "What is unobserved right now?"             → sectors with no live camera
 *
 * §11 walks this graph forward (rail → bus → parking → corridor → ghat →
 * exit). §12 walks it outward (incident → nearest unit → route → hospital).
 * Neither is honest without the edges being real, so the edges are built
 * from the entities' own declared relationships rather than hardcoded.
 *
 * ── HISTORICAL STATE ──
 * The brief asks for it explicitly. The twin keeps a bounded ring of past
 * snapshots supplied by the caller (useLiveModel already retains 180
 * minutes) rather than storing its own copy, so there is exactly one history
 * in the system and no chance of two that disagree.
 * ────────────────────────────────────────────────────────────────────────── */

import {
  AGENCIES, ENTITY_TYPES, REL,
  BRIDGES, JUNCTIONS, BARRICADES, CORRIDORS, BUS_TERMINALS, CAMPS,
  CAMERAS, PA_SYSTEMS, VMS_BOARDS, AMBULANCES, OFFICER_UNITS,
  HOSPITAL_CAPACITY,
  SECTORS, GHATS, ENTRY_GATES, EXIT_GATES, FACILITIES, ROUTES,
} from "./entities";

/* Facility layer → twin entity type. A facility whose layer is not mapped
 * here is skipped and counted, not silently dropped — an asset the twin
 * cannot classify is a gap in the catalogue and is reported as one. */
const FACILITY_TYPE = {
  hospitals: "hospital",
  medicalCamps: "medicalCamp",
  policePosts: "policePost",
  fireStations: "fireStation",
  parking: "parking",
  toilets: "toiletCluster",
  waterPoints: "waterStation",
  transportPoints: "railwayStation",
  commandCentres: "policePost",
  ambulancePoints: "medicalCamp",
  helpCentres: "medicalCamp",
};

const ROUTE_TYPE = {
  pedestrianRoutes: "corridor",
  vehicleRoutes: "road",
  diversionRoutes: "road",
  evacuationRoutes: "emergencyRoute",
};

/* ── Build the node set ────────────────────────────────────────────────── */
function buildNodes() {
  const nodes = new Map();
  const unclassified = [];

  const add = (e) => {
    if (nodes.has(e.id)) return;
    const meta = ENTITY_TYPES[e.type];
    nodes.set(e.id, {
      ...e,
      typeLabel: meta?.label,
      kind: meta?.kind ?? "unknown",
      /* An entity's agency can be overridden per-entity (an officer unit
       * carries its own), otherwise it comes from the type. */
      agency: e.agency ? AGENCIES[e.agency] ?? meta?.agency : meta?.agency,
      edges: [],
    });
  };

  SECTORS.forEach((s) => add({ ...s, type: "sector", coord: s.centre, sector: s.id }));
  GHATS.forEach((g) => add({ ...g, type: "ghat" }));
  [...ENTRY_GATES, ...EXIT_GATES].forEach((g) => add({ ...g, type: "gate" }));

  FACILITIES.forEach((f) => {
    const type = FACILITY_TYPE[f.layer];
    if (!type) { unclassified.push(f.id); return; }
    add({ ...f, type });
  });

  ROUTES.forEach((r) => {
    const type = ROUTE_TYPE[r.layer];
    if (!type) { unclassified.push(r.id); return; }
    add({ ...r, type, coord: r.coords?.[0] ?? null });
  });

  [
    ...BRIDGES, ...JUNCTIONS, ...BARRICADES, ...CORRIDORS, ...BUS_TERMINALS,
    ...CAMPS, ...CAMERAS, ...PA_SYSTEMS, ...VMS_BOARDS, ...AMBULANCES,
    ...OFFICER_UNITS,
  ].forEach(add);

  return { nodes, unclassified };
}

/* ── Build the edge set ────────────────────────────────────────────────
 * Edges come from what the entities themselves declare. An edge to a node
 * that does not exist is dropped and recorded as a dangling reference —
 * never created silently, because a twin that invents nodes to satisfy an
 * edge is a twin that will route an ambulance to a place with no address. */
function buildEdges(nodes) {
  const dangling = [];

  const link = (fromId, verb, toId) => {
    const from = nodes.get(fromId);
    const to = nodes.get(toId);
    if (!from || !to) { dangling.push({ from: fromId, verb, to: toId }); return; }
    from.edges.push({ verb, to: toId });
  };

  nodes.forEach((n) => {
    /* Everything sits within a sector. */
    if (n.sector && n.sector !== n.id) link(n.id, REL.WITHIN, n.sector);

    /* Corridors carry the movement chain. */
    if (n.type === "corridor" && n.from && n.to) {
      link(n.from, REL.FEEDS, n.id);
      link(n.id, REL.FEEDS, n.to);
    }
    /* Barricades constrain the corridor they sit on. */
    if (n.type === "barricade" && n.constrains) link(n.id, REL.CONSTRAINS, n.constrains);
    /* Cameras watch a specific thing. */
    if (n.type === "camera" && n.watches) link(n.id, REL.WATCHES, n.watches);
    /* Actuators address sectors. */
    if (Array.isArray(n.addresses)) n.addresses.forEach((s) => link(n.id, REL.ADDRESSES, s));
    /* Services and units serve their sector. */
    if ((n.kind === "service" || n.kind === "unit") && n.sector) {
      link(n.id, REL.SERVES, n.sector);
    }
    /* A ghat is drained by the exit gates in its sector. */
    if (n.type === "ghat") {
      EXIT_GATES.filter((g) => g.sector === n.sector)
        .forEach((g) => link(n.id, REL.EXITS_TO, g.id));
    }
  });

  return dangling;
}

/* ── Live state attachment ─────────────────────────────────────────────
 * The twin does not compute occupancy — the live model does. Attaching it
 * here rather than recomputing keeps a single source of truth, so the map,
 * the forecast and the twin can never disagree about how many people are in
 * a sector. */
function attachState(nodes, liveState) {
  if (!liveState) return;
  liveState.sectors.forEach((s) => {
    const node = nodes.get(s.id);
    if (!node) return;
    node.state = {
      current: s.current,
      capacity: s.capacity,
      occupancyPct: Math.round((s.current / s.capacity) * 100),
      netPerMin: s.netPerMin,
      inflowPerMin: s.inflowPerMin,
      egressCapacityPerMin: s.egressCapacityPerMin,
      ingressEgressRatio: s.ingressEgressRatio,
    };
  });

  /* Hospitals and camps carry their own capacity state. */
  Object.entries(HOSPITAL_CAPACITY).forEach(([id, cap]) => {
    const node = nodes.get(id);
    if (!node) return;
    node.state = {
      ...cap,
      bedsFree: cap.beds - cap.occupied,
      emergencyFree: cap.emergencyBays - cap.emergencyOccupied,
      occupancyPct: Math.round((cap.occupied / cap.beds) * 100),
    };
  });
}

/* ── The twin ──────────────────────────────────────────────────────────── */
export function buildTwin(liveState = null, history = []) {
  const { nodes, unclassified } = buildNodes();
  const dangling = buildEdges(nodes);
  attachState(nodes, liveState);

  const get = (id) => nodes.get(id) ?? null;
  const all = () => Array.from(nodes.values());
  const byType = (type) => all().filter((n) => n.type === type);
  const inSector = (sectorId) => all().filter((n) => n.sector === sectorId && n.id !== sectorId);

  const out = (id, verb) => {
    const n = nodes.get(id);
    if (!n) return [];
    return n.edges
      .filter((e) => !verb || e.verb === verb)
      .map((e) => ({ verb: e.verb, node: nodes.get(e.to) }))
      .filter((e) => e.node);
  };

  const incoming = (id, verb) =>
    all()
      .filter((n) => n.edges.some((e) => e.to === id && (!verb || e.verb === verb)))
      .map((n) => ({ verb, node: n }));

  /* Everything reachable downstream along FEEDS. Breadth-first with a seen
   * set, because a movement graph with a loop must terminate rather than
   * hang the command centre. */
  const downstream = (id, verb = REL.FEEDS, limit = 24) => {
    const seen = new Set([id]);
    const order = [];
    let frontier = [id];
    while (frontier.length && order.length < limit) {
      const nextFrontier = [];
      frontier.forEach((cur) => {
        out(cur, verb).forEach(({ node }) => {
          if (seen.has(node.id)) return;
          seen.add(node.id);
          order.push(node);
          nextFrontier.push(node.id);
        });
      });
      frontier = nextFrontier;
    }
    return order;
  };

  const upstream = (id, verb = REL.FEEDS, limit = 24) => {
    const seen = new Set([id]);
    const order = [];
    let frontier = [id];
    while (frontier.length && order.length < limit) {
      const nextFrontier = [];
      frontier.forEach((cur) => {
        incoming(cur, verb).forEach(({ node }) => {
          if (seen.has(node.id)) return;
          seen.add(node.id);
          order.push(node);
          nextFrontier.push(node.id);
        });
      });
      frontier = nextFrontier;
    }
    return order;
  };

  /* What closing or degrading this entity would affect. The answer a map
   * cannot give and the reason the graph exists. */
  const impactOf = (id) => {
    const node = get(id);
    if (!node) return null;
    const constrained = out(id, REL.CONSTRAINS).map((e) => e.node);
    const downstreamOfConstrained = constrained.flatMap((c) => downstream(c.id));
    const direct = downstream(id);
    const affected = [...new Set([...constrained, ...downstreamOfConstrained, ...direct])];
    const agencies = [...new Set(affected.map((a) => a.agency?.id).filter(Boolean))];
    return { entity: node, affected, agencies };
  };

  /* §16 input: which sectors have no camera the platform can actually read.
   * Every camera in this POC is disconnected, so the honest answer is "all
   * of them" — and the assurance layer says so rather than showing green. */
  const observationGaps = () =>
    SECTORS.map((s) => {
      const cams = byType("camera").filter((c) => c.sector === s.id);
      return {
        sectorId: s.id,
        name: s.name,
        cameras: cams.length,
        connected: cams.filter((c) => c.connected).length,
        observed: cams.some((c) => c.connected),
      };
    });

  return {
    nodes, get, all, byType, inSector, out, incoming,
    downstream, upstream, impactOf, observationGaps,
    history,
    /* Catalogue integrity, surfaced rather than swallowed. */
    integrity: {
      entityCount: nodes.size,
      typeCount: new Set(all().map((n) => n.type)).size,
      unclassified,
      dangling,
      /* The brief lists 24 entity types. Anything missing from the twin is
       * a coverage gap and is named, not hidden. */
      missingTypes: Object.keys(ENTITY_TYPES).filter(
        (t) => !all().some((n) => n.type === t),
      ),
    },
  };
}

export { AGENCIES, ENTITY_TYPES, REL };
